import React, { useEffect, useState } from 'react';
import Card from '@welldev-group/rcl-card';
import ContentLoaderPlaceholder from '@welldev-group/rcl-content-loader-placeholder';
import { I18nText, Link, EmphasisTag } from '@welldev-group/react-comp-lib';
import PropTypes from 'prop-types';
import { StringParam, withDefault, useQueryParams } from 'use-query-params';
import withQueryParamsProvider from '../../../../../shared/components/withQueryParamsProvider';
import routes from '../../../../../shared/routes';
import httpClient from '../../../../../shared/libraries/httpClient';
import EmptyTableContent from '../../../../../shared/components/EmptyTableContent';

const PlanTab = ({ id }) => {
  const [oldSubscriptions, setOldSubscriptions] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [redirectionUrls, setRedirectionUrls] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useQueryParams({
    tab: withDefault(StringParam, ''),
  });

  const { tab } = query;

  const fetchOldSubscriptions = async () => {
    const { data } = await httpClient.get(
      routes.subscriptions.old.list({
        affiliate_id: id,
      }),
    );
    setOldSubscriptions(data.oldSubscriptions);
    setCurrentPlan(data.currentPlan);
    setRedirectionUrls(data.urls);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchOldSubscriptions();
  }, []);

  useEffect(() => {
    setQuery({ tab: 'plans' }, 'replaceIn');
  }, [tab]);

  return (
    <Card version="v2">
      <div className="affiliate-plan-list">
        <div className="affiliate-plan-list__current-plan-header">
          <div className="col-lg-6 col-md-6 col-12 affiliate-plan-list__current-plan-header-texts">
            <div className="affiliate-plan-list__current-plan-header-texts-top">
              <I18nText id="affiliate_three_sixty.affiliate.tabs.plans.current_plan" />
            </div>
            {isLoading ? (
              <ContentLoaderPlaceholder
                numberOfLines={2}
                showBackground={false}
              />
            ) : (
              currentPlan && (
                <>
                  <div className="affiliate-plan-list__current-plan-header-texts-value">
                    <span>
                      {I18n.t(currentPlan.name, {
                        scope: 'components.plans',
                      })}
                    </span>{' '}
                    <EmphasisTag
                      text={currentPlan.period}
                      type="neutral"
                      size="tiny"
                    />
                  </div>
                  <div className="affiliate-plan-list__current-plan-header-texts-upgraded-on">
                    {I18n.t(
                      'components.affiliate_three_sixty.affiliate.tabs.plans.upgraded_on',
                      {
                        date: currentPlan.startedAt,
                      },
                    )}
                  </div>
                </>
              )
            )}
          </div>
          <div className="col-lg-6 col-md-6 col-12 col-bleed affiliate-plan-list__current-plan-header-plans-link">
            <Link
              href={redirectionUrls.billing}
              type="button"
              modifier="default"
              size="small"
            >
              <I18nText id="billing.header" />
            </Link>
            <Link href={redirectionUrls.plans} type="button" size="small">
              <I18nText id="affiliate_three_sixty.affiliate.tabs.plans.change_plan" />
            </Link>
          </div>
        </div>
        <div className="grid">
          <div className="col-12">
            <div className="col-4 col-bleed-y affiliate-plan-list__current-plan-header">
              <I18nText id="affiliate_three_sixty.affiliate.tabs.plans.history" />
            </div>
          </div>
          <div className="col-12 col-bleed-y">
            <div className="affiliate-plan-list__header">
              <div className="col-4 affiliate-plan-list__name-header">
                <I18nText id="affiliate_three_sixty.affiliate.tabs.plans.table.name" />
              </div>
              <div className="col-4 affiliate-plan-list__start-date-header">
                <I18nText id="affiliate_three_sixty.affiliate.tabs.plans.table.started_at" />
              </div>
              <div className="col-4 affiliate-plan-list__end-date-header">
                <I18nText id="affiliate_three_sixty.affiliate.tabs.plans.table.ended_at" />
              </div>
            </div>
          </div>
          <div className="col-12">
            {isLoading ? (
              <ContentLoaderPlaceholder
                numberOfLines={10}
                showBackground={false}
              />
            ) : (
              <>
                {oldSubscriptions.length > 0 &&
                  oldSubscriptions.map(oldSubscription => (
                    <div
                      className="affiliate-plan-list__row"
                      key={oldSubscription.id}
                    >
                      <div className="col-lg-4 col-md-4 col-12 affiliate-plan-list__name">
                        <span>
                          {I18n.t(oldSubscription.name, {
                            scope: 'components.plans',
                          })}
                        </span>{' '}
                        <EmphasisTag
                          text={oldSubscription.period}
                          type="neutral"
                          size="tiny"
                        />
                        <div className="affiliate-plan-list__header--small">
                          <I18nText id="affiliate_three_sixty.affiliate.tabs.plans.table.name" />
                        </div>
                      </div>
                      <div className="col-lg-4 col-md-4 col-6 affiliate-plan-list__start-date">
                        {oldSubscription.startedAt}
                        <div className="affiliate-plan-list__header--small">
                          <I18nText id="affiliate_three_sixty.affiliate.tabs.plans.table.started_at" />
                        </div>
                      </div>
                      <div className="col-lg-4 col-md-4 col-6 affiliate-plan-list__end-date">
                        {oldSubscription.endedAt}
                        <div className="affiliate-plan-list__header--small">
                          <I18nText id="affiliate_three_sixty.affiliate.tabs.plans.table.ended_at" />
                        </div>
                      </div>
                    </div>
                  ))}
              </>
            )}
            {!isLoading && !oldSubscriptions.length && <EmptyTableContent />}
          </div>
        </div>
      </div>
    </Card>
  );
};

PlanTab.propTypes = {
  id: PropTypes.number.isRequired,
};

export default withQueryParamsProvider(PlanTab);
